/**
 * Alta de contrato.
 *
 * Dos cosas gobiernan este formulario y las dos son de negocio, no de
 * interfaz:
 *
 * 1. **Cada modelo de facturación exige su precio.** La base lo obliga con
 *    una restricción (`ck_contract_pricing`) por una razón concreta: un
 *    contrato por equipo sin precio unitario **factura cero** y nadie se
 *    entera hasta el cierre del trimestre. Aquí el campo de precio cambia
 *    con el modelo y es obligatorio.
 * 2. **El preaviso decide el trabajo comercial.** No la fecha de fin. Con
 *    renovación tácita, pasarse del preaviso ata otro año entero.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from '../../forms/use-form.js';
import { CheckField, FormSection, SelectField, TextArea, TextField } from '../../forms/fields.js';
import { EntityPicker } from '../../forms/entity-picker.js';
import { isoDate, maxLength, required, type FieldRules, type FormValues } from '../../forms/form-state.js';

const BILLING_MODELS = [
  { value: 'FLAT', label: 'Cuota fija' },
  { value: 'PER_EQUIPMENT', label: 'Por equipo' },
  { value: 'PER_INSPECTION', label: 'Por revisión' },
  { value: 'PER_VISIT', label: 'Por visita' },
  { value: 'TIME_AND_MATERIALS', label: 'Horas y materiales' },
  { value: 'MIXED', label: 'Mixto' },
];

const PERIODICITIES = [
  { value: 'MONTHLY', label: 'Mensual' },
  { value: 'QUARTERLY', label: 'Trimestral' },
  { value: 'BIANNUAL', label: 'Semestral' },
  { value: 'ANNUAL', label: 'Anual' },
];

/**
 * Qué precio exige cada modelo.
 *
 * Es la misma tabla que la restricción `ck_contract_pricing` de la
 * migración 0010. Se repite aquí para poder avisar **antes** de enviar,
 * pero la que manda es la de la base: si divergieran, el servidor rechaza
 * y el formulario coloca el error en su campo.
 */
const PRICE_FIELD: Record<string, { field: string; label: string; help: string } | null> = {
  FLAT: {
    field: 'flatAmount',
    label: 'Importe de la cuota (€)',
    help: 'Lo que se factura cada periodo, independientemente del número de equipos.',
  },
  PER_EQUIPMENT: {
    field: 'pricePerEquipment',
    label: 'Precio por equipo (€)',
    help: 'Se multiplica por los equipos cubiertos en cada facturación.',
  },
  PER_INSPECTION: {
    field: 'pricePerInspection',
    label: 'Precio por revisión (€)',
    help: 'Se factura por acta emitida.',
  },
  PER_VISIT: { field: 'pricePerVisit', label: 'Precio por visita (€)', help: 'Se factura por desplazamiento.' },
  TIME_AND_MATERIALS: {
    field: 'hourlyRate',
    label: 'Precio por hora (€)',
    help: 'El material se factura aparte, al precio de tarifa.',
  },
  MIXED: null,
};

const RULES: FieldRules = {
  code: [required('El código'), maxLength(32)],
  name: [required('La denominación'), maxLength(255)],
  startDate: [required('La fecha de inicio'), isoDate()],
  endDate: [isoDate()],
};

/** Exige el precio que corresponde al modelo elegido. */
function priceRequired(values: FormValues): string | null {
  const model = String(values['billingModel'] ?? '');
  const price = PRICE_FIELD[model];
  if (!price) return null;

  const value = values[price.field];
  return value === null || value === '' || value === undefined ? price.field : null;
}

export function ContractFormScreen(): React.ReactNode {
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState('');
  const [customerError, setCustomerError] = useState<string | undefined>(undefined);

  const form = useForm<{ id: string }>({
    mode: 'create',
    rules: RULES,
    nullable: ['endDate', 'renewalMonths', 'slaHoursUrgent'],
    initial: {
      code: '',
      name: '',
      startDate: new Date().toISOString().slice(0, 10),
      endDate: '',
      billingModel: 'FLAT',
      billingPeriodicity: 'ANNUAL',
      flatAmount: '',
      pricePerEquipment: '',
      pricePerInspection: '',
      pricePerVisit: '',
      hourlyRate: '',
      includesCorrective: false,
      includesMaterials: false,
      includesTravel: false,
      coversAllCustomerSites: true,
      autoRenew: false,
      renewalMonths: '',
      renewalNoticeDays: 60,
      conditions: '',
      notes: '',
    },
    onSubmit: async (body, client) => {
      const payload: Record<string, unknown> = { ...body, customerId };

      // Los importes viajan como cadena decimal, que es lo que la API
      // espera: convertirlos a `number` aquí sería meter coma flotante en
      // el camino del dinero.
      for (const key of ['renewalMonths', 'renewalNoticeDays', 'slaHoursUrgent']) {
        if (typeof payload[key] === 'string' && payload[key] !== '') {
          payload[key] = Number(payload[key]);
        }
      }

      return client.post<{ id: string }>('/contracts', payload);
    },
    onSaved: () => void navigate('/contratos'),
  });

  const model = String(form.values['billingModel'] ?? 'FLAT');
  const price = PRICE_FIELD[model];
  const missingPrice = priceRequired(form.values);

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();

    if (customerId === '') {
      setCustomerError('Elija el cliente del contrato.');
      return;
    }
    setCustomerError(undefined);

    if (missingPrice !== null) {
      // Se avisa antes de enviar porque el mensaje del servidor sería el
      // de una restricción de base de datos, y eso no se le enseña a nadie.
      form.set(missingPrice, form.values[missingPrice] ?? '');
      return;
    }

    await form.submit();
  }

  return (
    <section className="screen">
      <header className="screen__head">
        <h1 className="screen__title">Nuevo contrato</h1>
        {form.dirty && <span className="screen__count">Cambios sin guardar</span>}
      </header>

      {form.formError !== null && (
        <p className="screen__error" role="alert">
          {form.formError}
        </p>
      )}

      {missingPrice !== null && price && (
        <p className="screen__error" role="alert">
          Un contrato «{BILLING_MODELS.find((m) => m.value === model)?.label}» necesita su{' '}
          {price.label.toLowerCase()}. Sin él <strong>facturaría cero</strong> y nadie se enteraría
          hasta el cierre del periodo.
        </p>
      )}

      {form.orphanErrors.length > 0 && (
        <p className="screen__error" role="alert">
          El servidor ha rechazado campos que este formulario no muestra:{' '}
          {form.orphanErrors.join(' · ')}
        </p>
      )}

      <form className="form" onSubmit={(e) => void submit(e)} noValidate>
        <FormSection title="Identificación">
          <EntityPicker
            label="Cliente"
            entity="CUSTOMER"
            value={customerId}
            onChange={(v) => {
              setCustomerId(v);
              setCustomerError(undefined);
            }}
            error={customerError}
            required
          />
          <TextField
            label="Código"
            value={String(form.values['code'] ?? '')}
            onChange={(v) => form.set('code', v)}
            onBlur={() => form.blur('code')}
            error={form.errors['code']}
            required
            maxLength={32}
          />
          <TextField
            label="Denominación"
            value={String(form.values['name'] ?? '')}
            onChange={(v) => form.set('name', v)}
            onBlur={() => form.blur('name')}
            error={form.errors['name']}
            required
            maxLength={255}
          />
          <TextField
            label="Fecha de inicio"
            type="date"
            value={String(form.values['startDate'] ?? '')}
            onChange={(v) => form.set('startDate', v)}
            onBlur={() => form.blur('startDate')}
            error={form.errors['startDate']}
            required
          />
          <TextField
            label="Fecha de fin"
            type="date"
            value={String(form.values['endDate'] ?? '')}
            onChange={(v) => form.set('endDate', v)}
            onBlur={() => form.blur('endDate')}
            error={form.errors['endDate']}
            help="Vacío = indefinido."
          />
          <CheckField
            label="Cubre todos los centros del cliente"
            checked={form.values['coversAllCustomerSites'] === true}
            onChange={(v) => form.set('coversAllCustomerSites', v)}
            help="Si se desmarca, habrá que asignar los centros uno a uno."
          />
        </FormSection>

        <FormSection
          title="Facturación"
          description="Cada modelo exige su precio. Un contrato sin el suyo factura cero."
        >
          <SelectField
            label="Modelo"
            value={model}
            onChange={(v) => form.set('billingModel', v)}
            options={BILLING_MODELS}
            required
          />
          <SelectField
            label="Periodicidad"
            value={String(form.values['billingPeriodicity'] ?? '')}
            onChange={(v) => form.set('billingPeriodicity', v)}
            options={PERIODICITIES}
            required
          />

          {price && (
            <TextField
              label={price.label}
              type="number"
              step="0.01"
              value={String(form.values[price.field] ?? '')}
              onChange={(v) => form.set(price.field, v)}
              error={form.errors[price.field]}
              required
              help={price.help}
            />
          )}

          {model === 'MIXED' && (
            <p className="field__help">
              El modelo mixto combina varios precios: rellene los que apliquen en las condiciones.
            </p>
          )}

          <CheckField
            label="Incluye correctivos"
            checked={form.values['includesCorrective'] === true}
            onChange={(v) => form.set('includesCorrective', v)}
          />
          <CheckField
            label="Incluye materiales"
            checked={form.values['includesMaterials'] === true}
            onChange={(v) => form.set('includesMaterials', v)}
          />
          <CheckField
            label="Incluye desplazamientos"
            checked={form.values['includesTravel'] === true}
            onChange={(v) => form.set('includesTravel', v)}
          />
        </FormSection>

        <FormSection
          title="Renovación"
          description={
            <>
              Con renovación tácita, <strong>pasarse del preaviso ata otro periodo entero</strong>. El
              sistema avisa cuando entra en la ventana, no cuando vence.
            </>
          }
        >
          <CheckField
            label="Renovación tácita"
            checked={form.values['autoRenew'] === true}
            onChange={(v) => form.set('autoRenew', v)}
          />
          <TextField
            label="Meses de renovación"
            type="number"
            value={String(form.values['renewalMonths'] ?? '')}
            onChange={(v) => form.set('renewalMonths', v)}
            help="Cuánto se prorroga cada vez. Vacío = el mismo plazo inicial."
          />
          <TextField
            label="Días de preaviso"
            type="number"
            value={String(form.values['renewalNoticeDays'] ?? '')}
            onChange={(v) => form.set('renewalNoticeDays', v)}
            help="El plazo para avisar de que no se renueva. Es lo que dispara la alerta comercial."
          />
        </FormSection>

        <FormSection title="Condiciones">
          <TextArea
            label="Condiciones particulares"
            value={String(form.values['conditions'] ?? '')}
            onChange={(v) => form.set('conditions', v)}
            rows={4}
          />
          <TextArea
            label="Notas internas"
            value={String(form.values['notes'] ?? '')}
            onChange={(v) => form.set('notes', v)}
          />
        </FormSection>

        <footer className="form__actions">
          <button className="button button--primary" type="submit" disabled={form.saving}>
            {form.saving ? 'Creando…' : 'Crear contrato'}
          </button>
          <button
            className="button button--quiet"
            type="button"
            onClick={() => {
              if (form.dirty && !window.confirm('Hay cambios sin guardar. ¿Salir igualmente?')) return;
              void navigate('/contratos');
            }}
          >
            Volver
          </button>
        </footer>
      </form>
    </section>
  );
}
